import React from 'react';
import { Card, Group, Stack, Text, SegmentedControl } from '@mantine/core';
import OptimizeEntry from '../optimization/OptimizeEntry';
import { useTranslation } from 'react-i18next';

function withinRange(e, range) {
  const s = new Date(e.start);
  const now = new Date();
  const startOfWeek = new Date(now);
  startOfWeek.setDate(now.getDate() - ((now.getDay() + 6) % 7)); // Monday start
  startOfWeek.setHours(0, 0, 0, 0);
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 7);

  if (range === 'this') {
    return s >= startOfWeek && s < endOfWeek;
  }
  if (range === 'next') {
    const nStart = new Date(endOfWeek);
    const nEnd = new Date(nStart);
    nEnd.setDate(nStart.getDate() + 7);
    return s >= nStart && s < nEnd;
  }
  if (range === '4w') {
    const fourW = new Date(startOfWeek);
    fourW.setDate(fourW.getDate() + 28);
    return s >= startOfWeek && s < fourW;
  }
  return true;
}

export default function PlanningSidebar({
  planningId,
  ownerId,
  isOwner,
  assignableUsers,
  user,
  events,
  optimizationProfile,
  optimizationOverrides,
  onLocalAssignmentsChange,
}) {
  const { t } = useTranslation('common');
  const [scope, setScope] = React.useState('this');

  const scopedEvents = React.useMemo(() => {
    return (events || []).filter((e) => withinRange(e, scope));
  }, [events, scope]);

  const metrics = React.useMemo(() => {
    const total = scopedEvents.length;
    let unassignedOrPartial = 0;
    for (const e of scopedEvents) {
      const rolesRequired = Array.isArray(e?.extendedProps?.rolesRequired) ? e.extendedProps.rolesRequired : [];
      const requiredSum = rolesRequired.reduce((s, r) => s + (Number(r.count) || 0), 0) || (Number(e?.extendedProps?.staffRequired) || 1);
      const assigned = Array.isArray(e?.extendedProps?.assignments) ? e.extendedProps.assignments.length : (
        Array.isArray(e?.extendedProps?.assignedTo) ? e.extendedProps.assignedTo.length : (e?.extendedProps?.assignedTo ? 1 : 0)
      );
      if (assigned < requiredSum) unassignedOrPartial += 1;
    }
    const conflicts = 0;
    return { total, unassignedOrPartial, conflicts };
  }, [scopedEvents]);

  return (
    <Stack>
      <Card withBorder radius="md" p="md">
        <Stack gap={8}>
          <Group justify="space-between" align="center">
            <Text fw={600}>{t('planning.sidebar.overview')}</Text>
            <SegmentedControl
              size="xs"
              value={scope}
              onChange={setScope}
              data={[
                { label: t('planning.sidebar.this_week'), value: 'this' },
                { label: t('planning.sidebar.next_week'), value: 'next' },
                { label: t('planning.sidebar.four_weeks'), value: '4w' },
              ]}
            />
          </Group>
          <Group justify="space-between"><Text c="dimmed">{t('planning.sidebar.events')}</Text><Text fw={600}>{metrics.total}</Text></Group>
          <Group justify="space-between"><Text c="dimmed">{t('planning.sidebar.unassigned')}</Text><Text fw={600}>{metrics.unassignedOrPartial}</Text></Group>
          <Group justify="space-between"><Text c="dimmed">{t('planning.sidebar.conflicts')}</Text><Text fw={600}>{metrics.conflicts}</Text></Group>
        </Stack>
      </Card>

      <Card withBorder radius="md" p="md">
        <Stack gap={8}>
          <Text fw={600}>{t('planning.sidebar.optimization')}</Text>
          {/* OptimizeEntry renders success/failure badges and buttons */}
          <OptimizeEntry
            planningId={planningId}
            ownerId={ownerId}
            isOwner={isOwner}
            assignableUsers={assignableUsers}
            user={user}
            events={events}
            optimizationProfile={optimizationProfile}
            optimizationOverrides={optimizationOverrides}
            onLocalAssignmentsChange={onLocalAssignmentsChange}
          />
          {/* When OptimizeEntry reports state, it includes badges. Keep a placeholder pill area */}
          <Group gap="xs">
            {/* Additional status pills can be slotted here if needed */}
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
